import React, { Fragment, useMemo } from 'react';

import { SeoContentView, SeoItemView } from '@zola/svc-marketplace-ts-types';
import H from '@zola/zola-ui/src/typography/Headings';

import cx from 'classnames';
import { renderToString } from 'react-dom/server';
import { FAQPage, Thing, WithContext } from 'schema-dts';

import { renderSchemas } from '~/util/seoUtils';

import SeoContent from './SeoContent';

import styles from './seoContentBlock.module.less';

const isListItem = (seoItem: SeoItemView): boolean => {
  return seoItem.type === 'LIST' && seoItem.htmlContent.length > 0;
};
const isParagraphItem = (seoItem: SeoItemView): boolean => {
  return seoItem.type === 'PARAGRAPH' && seoItem.htmlContent.length > 0;
};
const isSectionItem = (seoItem: SeoItemView): boolean => {
  return (
    seoItem.type === 'SECTION' && seoItem.children.length > 0 && seoItem.children.every(isListItem)
  );
};

const isSupportedItem = (seoItem: SeoItemView): boolean => {
  return isListItem(seoItem) || isParagraphItem(seoItem) || isSectionItem(seoItem);
};

type PreparedItem = {
  heading: string;
  reactContent: JSX.Element;
};

const prepareContentItem = (seoItem: SeoItemView): PreparedItem | null => {
  if (!isSupportedItem(seoItem)) return null;

  if (seoItem.type === 'PARAGRAPH') {
    return {
      heading: seoItem.heading,
      reactContent: (
        <Fragment>
          {seoItem.htmlContent.map((p, index) => {
            return <p key={`${index}`} dangerouslySetInnerHTML={{ __html: p }} />;
          })}
        </Fragment>
      ),
    };
  }

  if (seoItem.type === 'LIST') {
    return {
      heading: seoItem.heading,
      reactContent: (
        <ul className="links">
          {seoItem.htmlContent.map((content, index) => {
            return <li key={`${index}`} dangerouslySetInnerHTML={{ __html: content }} />;
          })}
        </ul>
      ),
    };
  }

  if (seoItem.type === 'SECTION') {
    return {
      heading: seoItem.heading,
      reactContent: (
        <ul className="section-list">
          {seoItem.children.map((child, index) => {
            return (
              <li key={index} className="section-item">
                <span className="section-item-heading">
                  <H.Title4 presentation="h5">
                    <b>{child.heading}</b> {child.subheading}
                  </H.Title4>
                </span>
                <ul className="section-content">
                  {child.htmlContent.map((content, childIndex) => {
                    return (
                      <li
                        className="section-content-item"
                        key={`${childIndex}`}
                        dangerouslySetInnerHTML={{ __html: content }}
                      />
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
      ),
    };
  }

  return null;
};

const getFaqSchemaEntry = (contentItem: SeoItemView): Thing | null => {
  const { heading, htmlContent, type } = contentItem;
  if (type === 'PARAGRAPH') {
    return {
      '@type': 'Question',
      name: heading,
      acceptedAnswer: {
        '@type': 'Answer',
        text: htmlContent.join(''),
      },
    };
  }
  if (type === 'LIST') {
    return {
      '@type': 'Question',
      name: heading,
      acceptedAnswer: {
        '@type': 'ItemList',
        itemListElement: htmlContent.map((content, index) => {
          return {
            '@type': 'ListItem',
            position: index,
            name: content,
          };
        }),
        itemListOrder: 'Descending',
        numberOfItems: htmlContent.length,
      },
    };
  }

  return null;
};

const getFaqSchema = (content: SeoItemView[]): WithContext<FAQPage> => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: content.map(getFaqSchemaEntry).filter((c): c is Thing => c !== null),
});

export const BackendSeoContentBlock = ({
  hed,
  seoContent,
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & { hed: string; seoContent: SeoContentView | null }) => {
  const supportedFaqItems = useMemo(() => {
    return (seoContent?.faqs || []).filter(isSupportedItem);
  }, [seoContent?.faqs]);

  const preparedFaqParagraphItems = useMemo(() => {
    return supportedFaqItems
      .filter((item) => item.type === 'PARAGRAPH')
      .map(prepareContentItem)
      .filter((item): item is PreparedItem => item !== null);
  }, [supportedFaqItems]);

  const preparedFaqListItems = useMemo(() => {
    return supportedFaqItems
      .filter((item) => item.type === 'LIST' || item.type === 'SECTION')
      .map(prepareContentItem)
      .filter((item): item is PreparedItem => item !== null);
  }, [supportedFaqItems]);

  const supportedEditorialItems = useMemo(() => {
    return (seoContent?.editorials || []).filter(isSupportedItem);
  }, [seoContent?.editorials]);

  const preparedEditorialItems = useMemo(() => {
    return supportedEditorialItems
      .map(prepareContentItem)
      .filter((item): item is PreparedItem => item !== null);
  }, [supportedEditorialItems]);

  if (
    preparedFaqParagraphItems.length === 0 &&
    preparedFaqListItems.length === 0 &&
    preparedEditorialItems.length === 0
  ) {
    return <Fragment />;
  }

  return (
    <div className={cx(className, styles.seoWrapper)} {...rest}>
      {renderSchemas([getFaqSchema(supportedFaqItems)])}
      <SeoContent
        className={styles.seoContent}
        heading={hed}
        contentItems={[
          ...preparedFaqParagraphItems,
          ...preparedEditorialItems,
          ...preparedFaqListItems,
        ].map((item) => {
          return {
            heading: item.heading,
            // You can't call render to string in a memo under next, so call that here
            htmlContent: renderToString(item.reactContent),
          };
        })}
      />
    </div>
  );
};
